import { injectable } from '~/container'
import { csrfTokenService } from '../csrf/CsrfTokenService'
import { mapPersonalDetailsResponse } from './mapResponse'
import { SubmitUserPersonalDetailsErrors, UserPersonalDetails } from './types'

@injectable()
export class UserPersonalDetailsRepositoryFetch {
  async get(): Promise<UserPersonalDetails> {
    const data = await $fetch<UserPersonalDetailsResponse>(
      '/_/fallback/_/users/user-personal-details/',
    )
    return mapPersonalDetailsResponse(data)
  }

  async submit(
    personalDetails: UserPersonalDetails,
  ): Promise<SubmitUserPersonalDetailsErrors | void> {
    const { csrfToken } = await csrfTokenService()
    try {
      await $fetch('/_/fallback/_/users/user-personal-details/', {
        method: 'POST',
        body: this.getFormData(personalDetails),
        headers: {
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      })
    } catch (e: any) {
      if (e.data?.errors) {
        return {
          errors: e.data.errors,
        }
      }

      throw e
    }
  }

  private getFormData(personalDetails: UserPersonalDetails) {
    const formData = new FormData()
    const birthDate = personalDetails.birthDate
      ? new Date(personalDetails.birthDate)
      : undefined
    formData.append(
      'address',
      this.formatFormDataValue(personalDetails.address),
    )
    formData.append('city', this.formatFormDataValue(personalDetails.city))
    formData.append('county', this.formatFormDataValue(personalDetails.county))
    formData.append(
      'postcode',
      this.formatFormDataValue(personalDetails.postcode),
    )
    formData.append(
      'country',
      this.formatFormDataValue(personalDetails.country),
    )
    formData.append(
      'telephone_0',
      this.formatFormDataValue(personalDetails.telephone?.countryDialingCode),
    )
    formData.append(
      'telephone_1',
      this.formatFormDataValue(personalDetails.telephone?.number),
    )
    formData.append(
      'unit_type',
      this.formatFormDataValue(personalDetails.unitType?.id),
    )
    formData.append(
      'dimensions_width',
      this.formatFormDataValue(personalDetails.dimensionsWidth),
    )
    formData.append(
      'dimensions_depth',
      this.formatFormDataValue(personalDetails.dimensionsDepth),
    )
    formData.append(
      'car_registration_number',
      this.formatFormDataValue(personalDetails.carRegistrationNumber),
    )
    formData.append(
      'names_of_all_party_members',
      this.formatFormDataValue(personalDetails.namesOfAllPartyMembers),
    )
    formData.append('gender', this.formatFormDataValue(personalDetails.gender))
    formData.append(
      'dob_day',
      this.formatFormDataValue(birthDate ? birthDate.getDate() : ''),
    )
    formData.append(
      'dob_month',
      this.formatFormDataValue(birthDate ? birthDate.getMonth() + 1 : ''),
    )
    formData.append(
      'dob_year',
      this.formatFormDataValue(birthDate ? birthDate.getFullYear() : ''),
    )
    return formData
  }

  private formatFormDataValue(value: any) {
    return value || ''
  }
}

interface UserPersonalDetailsResponse {
  address: string | null
  birthDate: string | null
  carRegistrationNumber: string | null
  city: string | null
  country: string
  county: string | null
  email: string
  gender: string
  namesOfAllPartyMembers: string
  postcode: string | null
  telephone: {
    countryDialingCode: string | null
    number: string
    countryDialingNumber?: string | null
  }
  typeAndApproxSizeOfUnit: string
}
